import {showMessage} from 'exsportia-components'
import {type IdTokenResult} from 'firebase/auth'
import {call, put, select, takeLeading} from 'redux-saga/effects'

import {PhoneLoginLinkMutationReturnType} from '../../api/auth/phone-login-link.mutation'
import {PhoneLoginVerifyMutationReturnType} from '../../api/auth/phone-login-verify.mutation'
import {SignUpMutationParams} from '../../api/auth/sign-up.mutation'
import {sentByServer} from '../../api/user/forgot-password'
import {API} from '../../networking/http'
import {store} from '../../redux/store'
import {
  auth,
  handlePasswordLogin,
  sendOtp,
  setAllowPhoneOnlyLogin,
  verifyOtp,
} from '../../utils/firebase'
import {l} from '../../utils/i18n'
import {setAuthToken} from '../../utils/local-storage'
import {queryClient} from '../../utils/react-query'
import {
  // addLeadRequestAction, //DEPRECATED
  basicActions,
  phoneLoginLinkEmailAction,
  phoneLoginResetAction,
  phoneLoginSendOtpAction,
  phoneLoginVerifyOtpAction,
  recoverPasswordRequestAction,
  removeAccountPayload,
  removeAccountRequestAction,
  resetPasswordConfirmRequestAction,
  signInRequestAction,
  signOutRequestAction,
  signUpRequestAction,
} from './actions'
import {AuthService} from './index'
import {removeAccountErrorKey} from './remove-account'
import {getRemoveAccountFailure} from './remove-account-error'
import {AuthStateMap} from './types'

type resType = {
  status: number
  data?: {
    message?: string
  }
}

export function* signInRequestSaga({
  payload: {email, venueId, password, callback, isAfterRegistration, isSocial},
}: ReturnType<typeof signInRequestAction>) {
  yield put(basicActions.isLoading(true))
  const queryRequest = () =>
    queryClient.fetchQuery(AuthService.queries.UserSignInCheckQuery({query: {email, venueId, isAfterRegistration}}))
  try {
    const res = (yield call(queryRequest)) as resType
    if (res.status && !isSocial && password) {
      yield call(handlePasswordLogin, email, password)
      callback?.()
    }
  } catch (e: unknown) {
    console.log((e as Error).message)
  } finally {
    yield put(basicActions.isLoading(false))
  }
}

export function* signOutRequestSaga() {
  yield put(basicActions.isLoading(true))
  try {
    yield call([auth, auth.signOut])
  } catch (e: unknown) {
    // catch
  } finally {
    yield put(basicActions.isLoading(false))
  }
}

export type removeAccountRes = {
  status: number
  data: string
}

// Customer self-service deletion. One call to DELETE /users/ (the server does the deleting); only when it answers
// 200 is the person signed out. A 409 `owner_of_open_venue` or any other failure leaves them signed in and is
// reported to the screen, so nothing looks deleted when it is not.
export function* removeAccountRequestSaga(payload: removeAccountPayload) {
  // TODO: move to mutation
  yield put(basicActions.isLoading(true))
  try {
    const endpoint = 'users/'
    const res = (yield call<(typeof API)['delete']>(API.delete, endpoint)) as removeAccountRes
    if (res.status === 200) {
      yield call([auth, auth.signOut])
      if (payload.callback) {
        payload.callback()
      }
    } else {
      payload.onError?.('failed')
    }
  } catch (e: unknown) {
    showMessage(l(removeAccountErrorKey(e)), 'error')
    payload.onError?.(getRemoveAccountFailure(e))
  } finally {
    yield put(basicActions.isLoading(false))
  }
}

export function* signUpRequestSaga({
  payload: {dob, email, phone, avatar, venueId, password, callback, lastName, firstName},
}: ReturnType<typeof signUpRequestAction>) {
  yield put(basicActions.isLoading(true))
  let body: SignUpMutationParams = {
    dob: dob ?? '',
    email,
    phone,
    venueId,
    password,
    lastName,
    firstName,
    dobStatus: 'awaiting',
    genderStatus: 'awaiting',
    lastNameStatus: 'awaiting',
    firstNameStatus: 'awaiting',
    name: `${firstName} ${lastName}`,
    role: ['USER'],
    withPassword: true,
  }
  if (avatar) {
    body = {
      ...body,
      avatar,
    }
  }
  const signUp = AuthService.queries.signUpMutation({}).mutationFn
  try {
    yield call(signUp, body)
    yield put(
      signInRequestAction({
        email,
        password,
        callback,
        isAfterRegistration: true,
      }),
    )
  } catch (e: unknown) {
    // catch
  } finally {
    yield put(basicActions.isLoading(false))
  }
}

export function* recoverPasswordRequestSaga({
  payload: {email, callback},
}: ReturnType<typeof recoverPasswordRequestAction>) {
  yield put(basicActions.isLoading(true))
  try {
    const sent: boolean = yield call(sentByServer, (url, body) => API.post(url, body), email)
    if (!sent) {
      yield call([auth, auth.sendPasswordResetEmail], email)
    }
    callback('done')
  } catch (e: unknown) {
    callback('wrongEmail')
  } finally {
    yield put(basicActions.isLoading(false))
  }
}

export function* resetPasswordConfirmRequestSaga({
  payload: {oobCode, callback, password, onError},
}: ReturnType<typeof resetPasswordConfirmRequestAction>) {
  yield put(basicActions.isLoading(true))
  try {
    yield call({context: auth, fn: auth.confirmPasswordReset}, String(oobCode), password)
    callback()
  } catch (e: unknown) {
    onError()
  } finally {
    yield put(basicActions.isLoading(false))
  }
}

// DEPRECATED
// export function* addLeadRequestSaga({
//   payload: {email, dob, avatar, phone, lastName, firstName, venueId, userId},
// }: ReturnType<typeof addLeadRequestAction>) {
//   yield put(basicActions.isLoading(true))
//   try {
//     const endpoint = `${APIDomain}members/${venueId}/addOneLead`
//     const options = {
//       leadData: {
//         dob,
//         email,
//         userId,
//         avatar,
//         lastName,
//         firstName,
//         phone: phone || '',
//         dobStatus: 'awaiting',
//         genderStatus: 'awaiting',
//         lastNameStatus: 'awaiting',
//         firstNameStatus: 'awaiting',
//         name: `${firstName} ${lastName}`,
//         isMember: true
//       },
//     }
//     yield call<any>(sendRequest, 'post', endpoint, options)
//   } catch (e: unknown) {
//     console.log(e)
//   }
// }

export function* phoneLoginSendOtpSaga({
  payload: {phoneNumber, callback},
}: ReturnType<typeof phoneLoginSendOtpAction>) {
  yield put(basicActions.phoneLoginLoading(true))
  yield put(basicActions.phoneLoginError(null))
  try {
    const confirmationResult: unknown = yield call(sendOtp, phoneNumber)
    yield put(basicActions.confirmationResult(confirmationResult))
    yield put(basicActions.phoneLoginStep('otp'))
    callback?.()
  } catch (e: unknown) {
    yield put(basicActions.phoneLoginError((e as Error).message))
  } finally {
    yield put(basicActions.phoneLoginLoading(false))
  }
}

export function* phoneLoginVerifyOtpSaga({
  payload: {otp, callback},
}: ReturnType<typeof phoneLoginVerifyOtpAction>): Generator<any, void, any> {
  yield put(basicActions.phoneLoginLoading(true))
  yield put(basicActions.phoneLoginError(null))
  const state: AuthStateMap = yield select((s: any) => s.auth)
  const confirmationResult = state.get('confirmationResult')
  try {
    const idToken: string = yield call(verifyOtp, confirmationResult, otp)
    yield put(basicActions.phoneIdToken(idToken))
    const verify = AuthService.queries.phoneLoginVerifyMutation({}).mutationFn
    const res: PhoneLoginVerifyMutationReturnType = yield call(verify, {idToken} as any)

    if (res.status === 'needs_email') {
      yield put(basicActions.phoneLoginStep('name'))
    } else if (res.customToken) {
      yield call(setAllowPhoneOnlyLogin, true)
      yield call([auth, auth.signInWithCustomToken], res.customToken)

      // Force Redux state update
      const currentUser = auth.currentUser
      if (currentUser) {
        const tokenData: IdTokenResult = yield call([currentUser, currentUser.getIdTokenResult], true)
        setAuthToken(tokenData)
        store.dispatch(AuthService.actions.isLoggedIn(true))
      }

      callback?.()
    }
  } catch (e: unknown) {
    yield put(basicActions.phoneLoginError((e as Error).message))
  } finally {
    yield put(basicActions.phoneLoginLoading(false))
  }
}

export function* phoneLoginLinkEmailSaga({
  payload: {email, firstName, lastName, dob, avatar, venueId, callback, onConflict},
}: ReturnType<typeof phoneLoginLinkEmailAction>): Generator<any, void, any> {
  yield put(basicActions.phoneLoginLoading(true))
  yield put(basicActions.phoneLoginError(null))
  const state: AuthStateMap = yield select((s: any) => s.auth)
  const phoneIdToken = state.get('phoneIdToken')
  try {
    const link = AuthService.queries.phoneLoginLinkMutation({}).mutationFn
    const res: PhoneLoginLinkMutationReturnType = yield call(link, {
      email,
      idToken: phoneIdToken,
      firstName,
      lastName,
      dob,
      avatar,
      venueId,
    } as any)

    if (res.customToken) {
      yield call(setAllowPhoneOnlyLogin, true)
      yield call([auth, auth.signInWithCustomToken], res.customToken)

      // Force Redux state update
      const currentUser = auth.currentUser
      if (currentUser) {
        const tokenData: IdTokenResult = yield call([currentUser, currentUser.getIdTokenResult], true)
        setAuthToken(tokenData)
        store.dispatch(AuthService.actions.isLoggedIn(true))
      }

      callback?.()
    }
  } catch (e: unknown) {
    const error = e as {
      message?: string
      response?: {
        data?: {
          code?: string
          message?: string
        }
      }
    }
    const apiCode = error.response?.data?.code

    if (apiCode === 'CONFLICT') {
      const conflictMessage =
        'This email is already associated with an account. Once logged in, you can add this phone number as a login method from Settings > Login Methods.'
      yield put(basicActions.phoneLoginError(conflictMessage))
      showMessage(conflictMessage, 'error')
      if (onConflict) {
        yield call(onConflict, email)
      }
      return
    }

    yield put(basicActions.phoneLoginError((e as Error).message))
  } finally {
    yield put(basicActions.phoneLoginLoading(false))
  }
}

export function* phoneLoginResetSaga() {
  yield put(basicActions.phoneLoginStep(null))
  yield put(basicActions.phoneLoginError(null))
  yield put(basicActions.phoneLoginLoading(false))
  yield put(basicActions.confirmationResult(null))
  yield put(basicActions.phoneIdToken(null))
}

export function* authWatcherSaga() {
  yield takeLeading(signInRequestAction, signInRequestSaga)
  yield takeLeading(signOutRequestAction, signOutRequestSaga)
  yield takeLeading(removeAccountRequestAction, function* (action) {
    yield removeAccountRequestSaga(action.payload)
  })
  yield takeLeading(signUpRequestAction, signUpRequestSaga)
  // yield takeLeading(addLeadRequestAction, addLeadRequestSaga) //DEPRECATED
  yield takeLeading(recoverPasswordRequestAction, recoverPasswordRequestSaga)
  yield takeLeading(resetPasswordConfirmRequestAction, resetPasswordConfirmRequestSaga)
  yield takeLeading(phoneLoginSendOtpAction, phoneLoginSendOtpSaga)
  yield takeLeading(phoneLoginVerifyOtpAction, phoneLoginVerifyOtpSaga)
  yield takeLeading(phoneLoginLinkEmailAction, phoneLoginLinkEmailSaga)
  yield takeLeading(phoneLoginResetAction, phoneLoginResetSaga)
}
